import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiWind, FiWifi, FiZap, FiCamera, 
  FiMapPin, FiCoffee, FiMusic, FiSun, 
  FiUser, FiHeart, FiShield, FiMaximize, FiUsers, FiVideo
} from 'react-icons/fi';
import { BiBuilding, BiRestaurant, BiCar, BiAccessibility } from 'react-icons/bi';
import { IoDiamondOutline } from 'react-icons/io5';
import './EventsFacilities.css';

const EVENT_CATEGORIES = ['All', 'Weddings', 'Social', 'Corporate', 'Cultural'];

const EVENTS_DATA = [
  {
    id: 1,
    title: 'Wedding Ceremonies',
    category: 'Weddings',
    description: 'Traditional and modern wedding setups with mandap decorations, floral arrangements, and sacred ceremony spaces.',
    capacity: '800-1200 guests',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 2,
    title: 'Wedding Receptions',
    category: 'Weddings',
    description: 'Grand reception halls with stage, lighting, and dining for an unforgettable celebration.',
    capacity: '1000+ guests',
    image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 3,
    title: 'Engagement Functions',
    category: 'Weddings',
    description: 'Intimate yet elegant spaces for ring ceremonies and engagement celebrations.',
    capacity: '200-500 guests',
    image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 4,
    title: 'Corporate Conferences',
    category: 'Corporate',
    description: 'Professional conference facilities with AV equipment, podiums, and breakout areas.',
    capacity: '500+ attendees',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 5,
    title: 'Birthday Celebrations',
    category: 'Social',
    description: 'Customizable party spaces with themed decorations and entertainment setups.',
    capacity: '100-500 guests',
    image: 'https://images.unsplash.com/photo-1530103862676-de8892b07439?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 6,
    title: 'Family Gatherings',
    category: 'Social',
    description: 'Warm, welcoming spaces perfect for family reunions, anniversaries, and get-togethers.',
    capacity: '200-800 guests',
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 7,
    title: 'Award Ceremonies',
    category: 'Corporate',
    description: 'Formal event spaces with professional stage, lighting, and seating arrangements.',
    capacity: '500+ guests',
    image: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 8,
    title: 'Cultural Events',
    category: 'Cultural',
    description: 'Versatile performance spaces for dance recitals, music concerts, and cultural programs.',
    capacity: '800+ guests',
    image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 9,
    title: 'Exhibitions',
    category: 'Corporate',
    description: 'Open floor plans ideal for trade shows, art exhibitions, and product launches.',
    capacity: 'Customizable',
    image: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 10,
    title: 'Seminars',
    category: 'Corporate',
    description: 'Focused learning environments with modern presentation technology.',
    capacity: '200-500 attendees',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=800&auto=format&fit=crop'
  }
];

const AMENITIES = [
  { icon: <FiWind />, name: 'AC Interiors' },
  { icon: <FiWifi />, name: 'Free WiFi' },
  { icon: <FiZap />, name: 'Generator Backup' },
  { icon: <FiCamera />, name: 'CCTV Security' },
  { icon: <BiBuilding />, name: 'Elevator' },
  { icon: <BiCar />, name: 'Ample Parking' },
  { icon: <BiRestaurant />, name: 'Catering Service' },
  { icon: <IoDiamondOutline />, name: 'Decoration' },
  { icon: <FiMusic />, name: 'Sound System' },
  { icon: <FiSun />, name: 'Ambient Lighting' },
  { icon: <FiUser />, name: 'Green Room' },
  { icon: <FiHeart />, name: 'Prayer Room' }
];

const SectionHeader = ({ title, subtitle }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  
  return (
    <motion.div 
      ref={ref}
      className="ef-section-header"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8 }}
    >
      <h2 className="ef-title">{title}</h2>
      <div className="ef-divider"></div>
      {subtitle && <p className="ef-subtitle">{subtitle}</p>}
    </motion.div>
  );
};

const EventsFacilities = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [heroRef, heroInView] = useInView({ triggerOnce: true });

  const filteredEvents = activeCategory === 'All' 
    ? EVENTS_DATA 
    : EVENTS_DATA.filter(event => event.category === activeCategory);

  return (
    <div className="events-facilities-page">
      {/* Hero Section */}
      <section className="ef-hero" ref={heroRef}>
        <motion.div 
          className="ef-hero-bg"
          initial={{ scale: 1.1 }}
          animate={heroInView ? { scale: 1 } : { scale: 1.1 }}
          transition={{ duration: 1.5 }}
          style={{ backgroundImage: 'url(/assets/main28.png)' }}
        />
        <div className="ef-hero-overlay"></div>
        <div className="ef-hero-content">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Events & Facilities
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Every Occasion Deserves Grandeur
          </motion.p>
        </div>
      </section>

      {/* Events Showcase */}
      <section className="ef-showcase-section">
        <div className="ef-container">
          <SectionHeader 
            title="Versatile Spaces for Every Event" 
            subtitle="Discover the perfect setting for your celebration or gathering." 
          />
          
          <div className="ef-filter-bar">
            {EVENT_CATEGORIES.map(category => (
              <button
                key={category}
                className={`ef-filter-btn ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <motion.div layout className="ef-events-grid">
            <AnimatePresence>
              {filteredEvents.map(event => (
                <motion.div
                  key={event.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  className="ef-event-card"
                >
                  <div className="ef-event-img-wrap">
                    <img src={event.image} alt={event.title} loading="lazy" />
                    <div className="ef-event-capacity">
                      <FiUsers /> {event.capacity}
                    </div>
                  </div>
                  <div className="ef-event-info">
                    <h3>{event.title}</h3>
                    <p>{event.description}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Facilities Highlights */}
      <section className="ef-facilities-section">
        <div className="ef-container">
          <SectionHeader 
            title="World-Class Facilities" 
            subtitle="Designed to deliver a seamless and luxurious experience." 
          />

          {/* Grand Hall */}
          <div className="ef-facility-feature full-width">
            <div className="ef-facility-img" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1519750157634-b6d493a0f77c?q=80&w=1200&auto=format&fit=crop)' }}>
              <div className="ef-facility-stats-overlay">
                <div className="stat">
                  <h4>50,000</h4>
                  <span>Sq.Ft Area</span>
                </div>
                <div className="stat">
                  <h4>1200+</h4>
                  <span>Capacity</span>
                </div>
                <div className="stat">
                  <h4>100%</h4>
                  <span>Air Conditioned</span>
                </div>
              </div>
            </div>
            <div className="ef-facility-text">
              <h3>The Grand Hall</h3>
              <p>An expansive pillar-less hall offering uninterrupted views, luxurious interiors, and immense flexibility for grand celebrations. Equipped with premium acoustics and ambient lighting.</p>
            </div>
          </div>

          {/* Dining Hall */}
          <div className="ef-facility-split">
            <div className="ef-split-text">
              <h3>Elegant Dining Hall</h3>
              <p>A dedicated dining space that comfortably seats 500+ guests simultaneously. Features multi-cuisine catering capabilities, hygienic food preparation areas, and elegant seating arrangements.</p>
              <ul className="ef-feature-list">
                <li><BiRestaurant /> Multi-cuisine Catering</li>
                <li><FiUsers /> 500+ Seating Capacity</li>
                <li><FiWind /> Fully Air-conditioned</li>
              </ul>
            </div>
            <div className="ef-split-img">
              <img src="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?q=80&w=800&auto=format&fit=crop" alt="Dining Hall" loading="lazy" />
            </div>
          </div>

          {/* Stage */}
          <div className="ef-facility-split reverse">
            <div className="ef-split-text">
              <h3>Professional Stage</h3>
              <p>A magnificent 40ft wide stage designed for spectacular performances and grand entries. Includes structural support for heavy backdrops and intricate floral arrangements.</p>
              <ul className="ef-feature-list">
                <li><FiMaximize /> 40ft Wide x 20ft Deep</li>
                <li><FiVideo /> LED Backdrop Ready</li>
                <li><FiSun /> Professional Stage Lighting</li>
              </ul>
            </div>
            <div className="ef-split-img">
              <img src="https://images.unsplash.com/photo-1478147427282-58a87a120781?q=80&w=800&auto=format&fit=crop" alt="Stage" loading="lazy" />
            </div>
          </div>

          {/* Bride & Groom Rooms */}
          <div className="ef-rooms-grid">
            <div className="ef-room-card">
              <img src="https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?q=80&w=600&auto=format&fit=crop" alt="Bridal Suite" loading="lazy" />
              <div className="ef-room-info">
                <h3>Bridal Suite</h3>
                <p>A luxurious private sanctuary for the bride with full-length mirrors, professional makeup lighting, AC, and attached washroom.</p>
              </div>
            </div>
            <div className="ef-room-card">
              <img src="https://images.unsplash.com/photo-1601058269550-1361c7784013?q=80&w=600&auto=format&fit=crop" alt="Groom Room" loading="lazy" />
              <div className="ef-room-info">
                <h3>Groom Room</h3>
                <p>A comfortable, well-appointed suite for the groom and entourage to relax and prepare, featuring plush seating and AC.</p>
              </div>
            </div>
          </div>

          {/* Other Facilities Grid */}
          <div className="ef-other-facilities">
            <div className="ef-other-card">
              <BiCar className="ef-other-icon" />
              <h4>Extensive Parking</h4>
              <p>Spacious parking area accommodating 500+ vehicles with dedicated valet services for convenience.</p>
            </div>
            <div className="ef-other-card">
              <BiAccessibility className="ef-other-icon" />
              <h4>Fully Accessible</h4>
              <p>Designed for everyone with wheelchair ramps, spacious elevators, and ground floor access.</p>
            </div>
            <div className="ef-other-card">
              <FiMusic className="ef-other-icon" />
              <h4>Lighting & Sound</h4>
              <p>Premium Bose/JBL sound systems and customizable ambient LED lighting throughout the venue.</p>
            </div>
            <div className="ef-other-card">
              <FiCoffee className="ef-other-icon" />
              <h4>Guest Facilities</h4>
              <p>Includes clean restrooms, comfortable lounge areas, a children's play zone, and a quiet prayer room.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Grid */}
      <section className="ef-amenities-section">
        <div className="ef-container">
          <SectionHeader 
            title="Comprehensive Amenities" 
            subtitle="Every detail considered for your comfort and convenience." 
          />
          <div className="ef-amenities-grid">
            {AMENITIES.map((amenity, idx) => (
              <motion.div 
                key={idx} 
                className="ef-amenity-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <div className="ef-amenity-icon">{amenity.icon}</div>
                <span>{amenity.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ef-cta-section">
        <div className="ef-cta-bg"></div>
        <div className="ef-cta-content">
          <h2>Ready to Host Your Dream Event?</h2>
          <p>Contact our event specialists to discuss your requirements and book your date.</p>
          <button className="ef-cta-btn">Plan Your Event With Us</button>
        </div>
      </section>
    </div>
  );
};

export default EventsFacilities;
